import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import { extendSubscription } from "../../api/getUserList";
import { commonErrorToastMessage, commonSuccessToastMessage } from "../../components/toastify/AllToastifyErrors";
import "./Utility.css";

/**
 * Format a date value for input[type="date"] (YYYY-MM-DD).
 */
const toDateInputValue = (value) => {
  if (value == null || value === "") return "";
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const ExtendExpiryModal = ({ open, onClose, user, onSuccess }) => {
  const [expiryDate, setExpiryDate] = useState("");
  const [formError, setFormError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      setExpiryDate(toDateInputValue(user.expiryDate));
      setFormError("");
    }
  }, [open, user]);

  const handleClose = () => {
    setExpiryDate("");
    setFormError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      commonErrorToastMessage("User not found.");
      return;
    }
    const trimmed = expiryDate?.trim();
    if (!trimmed) {
      setFormError("Expiry date is required.");
      return;
    }
    const d = new Date(trimmed);
    if (isNaN(d.getTime())) {
      setFormError("Please enter a valid date.");
      return;
    }
    setFormError("");
    setSubmitLoading(true);
    try {
      await extendSubscription(user.id, { newExpiryDate: trimmed });
      commonSuccessToastMessage("Expiry date updated successfully.");
      handleClose();
      onSuccess?.();
    } catch (err) {
      commonErrorToastMessage(
        err?.response?.data?.error || "Failed to update expiry date."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const displayName = user?.name ?? user?.email ?? "—";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#042739",
          color: "white",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Extend Subscription
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <div className="extend-subscription-name-group">
          <div className="extend-subscription-name-label">
            Name
          </div>
          <div className="extend-subscription-name-value">{displayName}</div>
        </div>
        <TextField
          margin="dense"
          label="Expiry Date"
          type="date"
          required
          fullWidth
          value={expiryDate}
          onChange={(e) => {
            setExpiryDate(e.target.value);
            if (formError) setFormError("");
          }}
          error={!!formError}
          helperText={formError}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: "#777E8D" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitLoading}
          sx={{
            backgroundColor: "#F4833D",
            "&:hover": { backgroundColor: "#e06a21" },
          }}
        >
          {submitLoading ? "Saving…" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtendExpiryModal;
